import {
  createContext,
  FC,
  MouseEvent,
  ReactNode,
  SVGProps,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { FocusTrap } from "focus-trap-react";
import { Link, useLocation } from "react-router-dom";

import Footer from "@/components/footer";
import { useProviderMagicLink } from "@/hooks/provider/useProviderMagicLink";
import { useLoggedInUser } from "@/hooks/useLoggedInUser";
import { cn } from "@/utils/cn";

type ProviderPortalShellProps = {
  children: ReactNode;
};

type ProviderOrderNavigationGuard = {
  isDirty: boolean;
  requestExit: (destination: string) => void;
};

type ProviderPortalNavigationContextValue = {
  registerOrderNavigationGuard: (
    guard: ProviderOrderNavigationGuard | null
  ) => void;
};

const ProviderPortalNavigationContext =
  createContext<ProviderPortalNavigationContextValue>({
    registerOrderNavigationGuard: () => undefined,
  });

export const useProviderPortalNavigationGuard = () =>
  useContext(ProviderPortalNavigationContext);

const navItems = [
  {
    label: "Order a test",
    path: "/provider/order",
    icon: PlusNavIcon,
  },
  {
    label: "Test Tracker",
    path: "/provider/tests",
    icon: TestTubeIcon,
  },
  {
    label: "Resources",
    path: "/provider/resources",
    icon: GraduationCapIcon,
  },
];

export const ProviderPortalShell = ({ children }: ProviderPortalShellProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [headerHovered, setHeaderHovered] = useState(false);
  const [providerLinkModalOpen, setProviderLinkModalOpen] = useState(false);
  const providerLink = useProviderMagicLink();
  const [linkCopied, setLinkCopied] = useState(false);
  const location = useLocation();
  const orderNavigationGuardRef = useRef<ProviderOrderNavigationGuard | null>(
    null
  );
  const registerOrderNavigationGuard = useCallback(
    (guard: ProviderOrderNavigationGuard | null) => {
      orderNavigationGuardRef.current = guard;
    },
    []
  );
  const isOrderPage = location.pathname.startsWith("/provider/order");
  const currentUser = useLoggedInUser();
  const initials =
    `${currentUser.identity.firstName?.[0] ?? ""}${currentUser.identity.lastName?.[0] ?? ""}` ||
    currentUser.identity.email?.[0] ||
    "E";
  const displayName =
    [currentUser.identity.firstName, currentUser.identity.lastName]
      .filter(Boolean)
      .join(" ") || "Provider";

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileMenuOpen]);

  const copyProviderLink = async () => {
    if (!providerLink) return;

    try {
      await navigator.clipboard.writeText(providerLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch (e) {
      setLinkCopied(false);
    }
  };

  const handleNavigationClick = (
    event: MouseEvent<HTMLAnchorElement>,
    path: string
  ) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const currentLocation = `${location.pathname}${location.search}`;
    if (orderNavigationGuardRef.current?.isDirty && currentLocation !== path) {
      event.preventDefault();
      setMobileMenuOpen(false);
      orderNavigationGuardRef.current.requestExit(path);
    }
  };

  const nav = (collapsed = false, mobile = false) => (
    <nav className="flex flex-col gap-0">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname.startsWith(item.path);

        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={(event) => {
              handleNavigationClick(event, item.path);
              if (!event.defaultPrevented) setMobileMenuOpen(false);
            }}
            tabIndex={mobile && !mobileMenuOpen ? -1 : undefined}
            aria-label={item.label}
            onMouseEnter={() => {
              if (!mobile) setDesktopCollapsed(false);
            }}
            onFocus={() => {
              if (!mobile) setDesktopCollapsed(false);
            }}
            className={cn(
              "flex items-center border-l-4 text-[13px] font-semibold uppercase tracking-[0.05em] transition",
              mobile ? "h-[56px] gap-4 px-6" : "h-[64px] gap-4 px-5",
              collapsed && "justify-center px-0",
              isActive
                ? "border-evvy-blue text-evvy-blue"
                : "border-transparent text-white hover:bg-white/10"
            )}
          >
            <Icon className="h-6 w-6 shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen w-full bg-evvy-cream text-evvy-black lg:flex">
      <aside
        className={cn(
          "fixed left-0 top-0 hidden h-screen shrink-0 bg-evvy-black text-white transition-[width] duration-200 lg:flex lg:flex-col lg:justify-between lg:py-8",
          desktopCollapsed ? "w-[96px]" : "w-[325px]"
        )}
      >
        <div>
          <div
            className={cn(
              "mb-12 flex items-center justify-between px-6",
              desktopCollapsed && "justify-center px-0"
            )}
            onMouseEnter={() => setHeaderHovered(true)}
            onMouseLeave={() => setHeaderHovered(false)}
          >
            {desktopCollapsed ? (
              <button
                type="button"
                className="rounded-full p-1 text-white hover:bg-white/10"
                aria-label="Expand navigation"
                onClick={() => setDesktopCollapsed(false)}
              >
                {headerHovered ? (
                  <PanelLeftIcon className="h-6 w-6 rotate-180 transition-transform" />
                ) : (
                  <img
                    src="/images/evvy-logo-text-white.svg"
                    alt="Evvy"
                    className="h-8 w-[72px]"
                  />
                )}
              </button>
            ) : (
              <>
                <Link
                  to="/provider/order"
                  className="block"
                  onClick={(event) =>
                    handleNavigationClick(event, "/provider/order")
                  }
                >
                  <img
                    src="/images/evvy-logo-text-white.svg"
                    alt="Evvy"
                    className="h-8 w-[72px]"
                  />
                </Link>
                <button
                  type="button"
                  className="rounded-full p-1 text-white hover:bg-white/10"
                  aria-label="Collapse navigation"
                  onClick={() => setDesktopCollapsed(true)}
                >
                  <PanelLeftIcon className="h-6 w-6 transition-transform" />
                </button>
              </>
            )}
          </div>
          {nav(desktopCollapsed)}
        </div>

        <div className="px-6">
          <div className="mb-6 w-full border-t border-dashed border-white/70" />
          <div
            className={cn(
              "flex items-center justify-between gap-4",
              desktopCollapsed && "justify-center"
            )}
          >
            <Link
              to="/provider/account"
              onClick={(event) =>
                handleNavigationClick(event, "/provider/account")
              }
              className={cn(
                "flex min-w-0 items-center gap-4 text-white hover:text-evvy-blue",
                desktopCollapsed && "hidden"
              )}
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-evvy-blue text-sm font-medium text-evvy-black">
                {initials.toUpperCase()}
              </div>
              <span className="truncate text-lg">{displayName}</span>
            </Link>
            {desktopCollapsed ? (
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-evvy-blue text-sm font-medium text-evvy-black"
                title={displayName}
                onClick={() => setDesktopCollapsed(false)}
              >
                {initials.toUpperCase()}
              </button>
            ) : (
              <button
                type="button"
                className="shrink-0 text-white hover:text-evvy-blue"
                aria-label="Open provider link"
                onClick={() => setProviderLinkModalOpen(true)}
              >
                <LinkIcon className="h-6 w-6" />
              </button>
            )}
          </div>
        </div>
      </aside>

      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col lg:transition-[margin-left] lg:duration-200",
          desktopCollapsed ? "lg:ml-[96px]" : "lg:ml-[325px]"
        )}
      >
        <header
          className={cn(
            "sticky top-0 z-30 flex items-center justify-between bg-evvy-black px-4 py-3 text-white transition-opacity duration-300 lg:hidden",
            mobileMenuOpen && "pointer-events-none opacity-0"
          )}
        >
          <button
            type="button"
            className="rounded-full p-2 hover:bg-white/10"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <Link
            to="/provider/order"
            className="absolute left-1/2 block -translate-x-1/2"
            onClick={(event) => handleNavigationClick(event, "/provider/order")}
          >
            <img
              src="/images/evvy-logo-text-white.svg"
              alt="Evvy"
              className="h-8 w-[72px]"
            />
          </Link>
          <span className="h-10 w-10" aria-hidden="true" />
        </header>

        <div
          className={cn(
            "fixed inset-0 z-40 bg-transparent transition-opacity duration-300 lg:hidden",
            mobileMenuOpen
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0"
          )}
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
        />

        <FocusTrap active={mobileMenuOpen}>
          <aside
            className={cn(
              "fixed inset-y-0 left-0 z-50 flex w-[83vw] max-w-[325px] flex-col bg-evvy-black py-8 text-white shadow-2xl transition-transform duration-300 ease-out lg:hidden",
              mobileMenuOpen
                ? "translate-x-0"
                : "pointer-events-none -translate-x-full"
            )}
            role="dialog"
            aria-modal="true"
            aria-label="Provider navigation"
            aria-hidden={!mobileMenuOpen}
          >
            <div className="flex items-center justify-between px-6">
              <Link
                to="/provider/order"
                className="block"
                onClick={(event) => {
                  handleNavigationClick(event, "/provider/order");
                  if (!event.defaultPrevented) setMobileMenuOpen(false);
                }}
                tabIndex={mobileMenuOpen ? undefined : -1}
              >
                <img
                  src="/images/evvy-logo-text-white.svg"
                  alt="Evvy"
                  className="h-8 w-[72px]"
                />
              </Link>
              <button
                type="button"
                className="rounded-full p-2 hover:bg-white/10"
                aria-label="Close menu"
                onClick={() => setMobileMenuOpen(false)}
                tabIndex={mobileMenuOpen ? undefined : -1}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mt-12">{nav(false, true)}</div>

            <div className="mt-auto px-6">
              <div className="mb-6 w-full border-t border-dashed border-white/70" />
              <button
                type="button"
                className="unset flex w-full items-center justify-between gap-4 text-left normal-case"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setProviderLinkModalOpen(true);
                }}
                tabIndex={mobileMenuOpen ? undefined : -1}
              >
                <span className="flex min-w-0 items-center gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-evvy-blue text-sm font-medium text-evvy-black">
                    {initials.toUpperCase()}
                  </span>
                  <span className="truncate text-lg">{displayName}</span>
                </span>
                <LinkIcon className="h-6 w-6 shrink-0" />
              </button>
            </div>
          </aside>
        </FocusTrap>

        <main className="min-w-0 flex-1">
          <ProviderPortalNavigationContext.Provider
            value={{ registerOrderNavigationGuard }}
          >
            {children}
          </ProviderPortalNavigationContext.Provider>
        </main>
        {!isOrderPage && <Footer variant="provider-revamp" />}
      </div>

      {providerLinkModalOpen && (
        <ProviderLinkModal
          providerLink={providerLink}
          copied={linkCopied}
          onCopy={copyProviderLink}
          onClose={() => setProviderLinkModalOpen(false)}
        />
      )}
    </div>
  );
};

const ProviderLinkModal = ({
  providerLink,
  copied,
  onCopy,
  onClose,
}: {
  providerLink: string;
  copied: boolean;
  onCopy: () => void;
  onClose: () => void;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-md">
    <div className="relative w-full max-w-[518px] rounded-[20px] bg-white p-8 md:p-12">
      <button
        type="button"
        className="absolute right-5 top-5 rounded-full p-1 hover:bg-evvy-cream"
        aria-label="Close provider link modal"
        onClick={onClose}
      >
        <XMarkIcon className="h-6 w-6" />
      </button>
      <div className="mb-8 text-center">
        <h2 className="mb-4 text-3xl font-[350] leading-[1.2]">
          Share Your Evvy Provider Link
        </h2>
        <p className="mb-0 text-base text-evvy-black">
          Share this unique link with patients. They can purchase an Evvy test
          directly and you&apos;ll automatically get the results.
        </p>
      </div>
      <button
        type="button"
        className="unset mb-8 flex w-full items-center gap-2 rounded-xl border border-gray-200 bg-[#F9F8F7] px-3 py-2 text-left text-base font-normal normal-case leading-[1.5] text-gray-500"
        onClick={onCopy}
        disabled={!providerLink}
      >
        <CopyIcon className="h-6 w-6 shrink-0 text-gray-500" />
        <span className="min-w-0 flex-1 truncate">
          {copied ? "Link copied!" : providerLink || "Generating..."}
        </span>
      </button>
      <div className="rounded-xl bg-evvy-cream px-4 py-6 text-center">
        <div className="mb-2 text-[13px] font-semibold uppercase tracking-[0.05em]">
          Helpful Tip
        </div>
        <p className="mb-2 text-base">
          Send a message to your patients explaining how Evvy and our testing
          works.
        </p>
        <a
          href="https://support.evvy.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm underline"
        >
          See examples →
        </a>
      </div>
    </div>
  </div>
);

type IconProps = SVGProps<SVGSVGElement>;

const BaseIcon: FC<IconProps> = ({ children, className, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    {...props}
  >
    {children}
  </svg>
);

function PlusNavIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </BaseIcon>
  );
}

function TestTubeIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M21.9069 6.54545L6.34434 22.1081C5.76768 22.6789 4.98004 22.9997 4.1589 22.9978C3.33776 22.9959 2.55161 22.6711 1.97707 22.0972C1.40113 21.5213 1.09091 20.7309 1.09091 19.9091C1.09091 19.0873 1.40113 18.2969 1.97707 17.721L17.5251 2.18182M16.3615 1.09091L22.9978 7.63636M11.9978 16.3636H3.27071" />
    </BaseIcon>
  );
}

function GraduationCapIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M22 10 12 5 2 10l10 5 10-5Z" />
      <path d="M6 12v5c3 2 9 2 12 0v-5" />
      <path d="M22 10v6" />
    </BaseIcon>
  );
}

function PanelLeftIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d="m16 9-3 3 3 3" />
    </BaseIcon>
  );
}

function LinkIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <path d="M10 13a5 5 0 0 0 7.07 0l2.12-2.12a5 5 0 0 0-7.07-7.07L11 4.93" />
      <path d="M14 11a5 5 0 0 0-7.07 0L4.81 13.12a5 5 0 0 0 7.07 7.07L13 19.07" />
    </BaseIcon>
  );
}

function CopyIcon(props: IconProps) {
  return (
    <BaseIcon {...props}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </BaseIcon>
  );
}
